import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dronesAPI, projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Plus, Trash2, Edit2, Plane, Calendar as CalendarIcon, FileText, Shield, Upload, X, Package } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = '/api';

const DronesPage = () => {
  const { isSuperAdmin, user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [drones, setDrones] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editDrone, setEditDrone] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileInputRef = useRef(null);

  const [projectId, setProjectId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [model, setModel] = useState('');
  const [certifications, setCertifications] = useState([]);
  const [newCertification, setNewCertification] = useState('');
  const [insuranceExpiry, setInsuranceExpiry] = useState(null);
  const [droneItems, setDroneItems] = useState([]);

  const fetchData = async () => {
    try {
      const [dronesRes, projectsRes] = await Promise.all([
        dronesAPI.list(),
        isSuperAdmin ? projectsAPI.list() : Promise.resolve({ data: [] })
      ]);
      setDrones(dronesRes.data);
      setProjects(projectsRes.data);
    } catch (error) { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [isSuperAdmin]);

  const resetForm = () => {
    setProjectId(user?.project_id || '');
    setSerialNumber('');
    setModel('');
    setCertifications([]);
    setNewCertification('');
    setInsuranceExpiry(null);
    setDroneItems([]);
  };

  const openCreateDialog = () => { resetForm(); setEditDrone(null); setCreateOpen(true); };

  const openEditDialog = (drone) => {
    setProjectId(drone.project_id);
    setSerialNumber(drone.serial_number);
    setModel(drone.model);
    setCertifications(drone.certifications || []);
    setInsuranceExpiry(drone.insurance_expiry ? new Date(drone.insurance_expiry) : null);
    setDroneItems(drone.items || []);
    setEditDrone(drone);
    setCreateOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const droneData = {
      project_id: isSuperAdmin ? projectId : user.project_id,
      serial_number: serialNumber,
      model,
      certifications,
      insurance_expiry: insuranceExpiry?.toISOString() || null,
      documents: editDrone?.documents || [],
      items: droneItems.filter(item => item.name)
    };
    try {
      if (editDrone) {
        await dronesAPI.update(editDrone.id, droneData);
        toast.success('Drone atualizado!');
      } else {
        await dronesAPI.create(droneData);
        toast.success('Drone cadastrado!');
      }
      setCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao salvar drone'); }
  };

  const handleDelete = async (droneId) => {
    if (!window.confirm('Tem certeza que deseja excluir este drone?')) return;
    try {
      await dronesAPI.delete(droneId);
      toast.success('Drone excluido!');
      fetchData();
    } catch (error) { toast.error('Erro ao excluir drone'); }
  };

  const handleUploadDocument = async (droneId, file) => {
    if (!file) return;
    setUploadingFor(droneId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/drones/${droneId}/upload-document`, formData, {
        headers
      });
      toast.success('Documento anexado ao drone!');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao anexar documento'); }
    finally { setUploadingFor(null); }
  };

  const addCertification = () => {
    if (newCertification && !certifications.includes(newCertification)) {
      setCertifications([...certifications, newCertification]);
      setNewCertification('');
    }
  };

  const isExpiringSoon = (date) => {
    if (!date) return false;
    const diff = new Date(date) - new Date();
    return diff < 30 * 24 * 60 * 60 * 1000 && diff > 0;
  };
  const isExpired = (date) => date && new Date(date) < new Date();
  const getProjectName = (id) => projects.find(p => p.id === id)?.name || 'Projeto';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="drones-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
          <Plane size={20} className="text-gold" /> GESTAO DE DRONES
        </h2>
        {isSuperAdmin && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" onClick={openCreateDialog} data-testid="create-drone-btn">
                <Plus size={18} className="mr-2" /> NOVO DRONE
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gold font-rajdhani uppercase tracking-wider">
                  {editDrone ? 'EDITAR DRONE' : 'CADASTRAR NOVO DRONE'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                {isSuperAdmin && (
                  <div className="space-y-2">
                    <Label className="text-gray-500 font-mono text-xs uppercase">Projeto</Label>
                    <Select value={projectId} onValueChange={setProjectId} required>
                      <SelectTrigger className="input-hud" data-testid="drone-project-select"><SelectValue placeholder="Selecione o projeto" /></SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200">
                        {projects.map((p) => <SelectItem key={p.id} value={p.id} className="text-navy hover:bg-gold/10">{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-500 font-mono text-xs uppercase">Modelo</Label>
                    <Input value={model} onChange={(e) => setModel(e.target.value)} className="input-hud" placeholder="Ex: DJI Matrice 30T" required data-testid="drone-model-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-500 font-mono text-xs uppercase">Numero de Serie</Label>
                    <Input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} className="input-hud" placeholder="Ex: DJI-M30-001" required data-testid="drone-serial-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 font-mono text-xs uppercase">Vencimento do Seguro</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal bg-gray-50 border-gray-200 hover:bg-gray-100" data-testid="insurance-expiry-btn">
                        <CalendarIcon className="mr-2 h-4 w-4 text-gold" />
                        {insuranceExpiry ? format(insuranceExpiry, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white border border-gray-200" align="start">
                      <Calendar mode="single" selected={insuranceExpiry} onSelect={setInsuranceExpiry} locale={ptBR} className="bg-white" />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 font-mono text-xs uppercase">Certificacoes</Label>
                  <div className="flex gap-2">
                    <Input value={newCertification} onChange={(e) => setNewCertification(e.target.value)} className="input-hud flex-1" placeholder="Ex: ANAC, DECEA" />
                    <Button type="button" onClick={addCertification} className="btn-secondary"><Plus size={16} /></Button>
                  </div>
                  {certifications.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {certifications.map((cert, i) => (
                        <span key={i} className="badge-info flex items-center gap-1">
                          <Shield size={12} />{cert}
                          <button type="button" onClick={() => setCertifications(certifications.filter(c => c !== cert))}><X size={12} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Items / Equipamentos */}
                <div className="space-y-3">
                  <Label className="text-gray-500 font-mono text-xs uppercase flex items-center gap-2">
                    <Package size={14} className="text-gold" /> Itens / Equipamentos
                  </Label>
                  {droneItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`drone-item-row-${idx}`}>
                      <div className="col-span-3">
                        {idx === 0 && <span className="text-[10px] text-gray-400 font-mono">ITEM</span>}
                        <Input value={item.name || ''} onChange={(e) => { const arr = [...droneItems]; arr[idx] = {...arr[idx], name: e.target.value}; setDroneItems(arr); }} className="input-hud text-sm" placeholder="Ex: Bateria" data-testid={`item-name-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        {idx === 0 && <span className="text-[10px] text-gray-400 font-mono">QTD</span>}
                        <Input type="number" value={item.quantity || ''} onChange={(e) => { const arr = [...droneItems]; arr[idx] = {...arr[idx], quantity: e.target.value}; setDroneItems(arr); }} className="input-hud text-sm" placeholder="0" data-testid={`item-qty-${idx}`} />
                      </div>
                      <div className="col-span-3">
                        {idx === 0 && <span className="text-[10px] text-gray-400 font-mono">SERIAL</span>}
                        <Input value={item.serial || ''} onChange={(e) => { const arr = [...droneItems]; arr[idx] = {...arr[idx], serial: e.target.value}; setDroneItems(arr); }} className="input-hud text-sm" placeholder="Serial" data-testid={`item-serial-${idx}`} />
                      </div>
                      <div className="col-span-3">
                        {idx === 0 && <span className="text-[10px] text-gray-400 font-mono">OBS</span>}
                        <Input value={item.observation || ''} onChange={(e) => { const arr = [...droneItems]; arr[idx] = {...arr[idx], observation: e.target.value}; setDroneItems(arr); }} className="input-hud text-sm" placeholder="Observacao" data-testid={`item-obs-${idx}`} />
                      </div>
                      <div className="col-span-1">
                        <Button type="button" variant="ghost" size="icon" className="text-red-400 hover:text-red-600 hover:bg-red-50 h-9 w-9" onClick={() => setDroneItems(droneItems.filter((_, i) => i !== idx))} data-testid={`remove-item-${idx}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button type="button" variant="outline" className="w-full border-dashed border-gray-300 text-gray-500 hover:text-gold hover:border-gold text-xs" onClick={() => setDroneItems([...droneItems, { name: '', quantity: '', serial: '', observation: '' }])} data-testid="add-drone-item-btn">
                    <Plus size={14} className="mr-1" /> ADICIONAR ITEM
                  </Button>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="btn-secondary flex-1">CANCELAR</Button>
                  <Button type="submit" className="btn-primary flex-1" data-testid="save-drone-btn">{editDrone ? 'ATUALIZAR' : 'CADASTRAR'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {drones.map((drone) => (
          <div key={drone.id} className="card-hud p-4 space-y-4" data-testid={`drone-card-${drone.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Plane size={16} className="text-gold" />
                  <h3 className="text-navy font-rajdhani font-semibold uppercase">{drone.serial_number}</h3>
                </div>
                <p className="text-blue-600 text-sm font-mono">{drone.model}</p>
              </div>
              <div className="flex gap-1">
                {isSuperAdmin && (
                  <>
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(drone)} className="text-blue-600 hover:text-gold hover:bg-gold/10" data-testid={`edit-drone-${drone.id}`}>
                      <Edit2 size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(drone.id)} className="text-gray-500 hover:text-red-500 hover:bg-red-50" data-testid={`delete-drone-${drone.id}`}>
                      <Trash2 size={16} />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {isSuperAdmin && <div className="text-gray-500 text-xs font-mono">Projeto: {getProjectName(drone.project_id)}</div>}

            {drone.insurance_expiry && (
              <div className="flex items-center gap-2">
                <Shield size={14} className={isExpired(drone.insurance_expiry) ? 'text-red-500' : isExpiringSoon(drone.insurance_expiry) ? 'text-gold' : 'text-green-600'} />
                <span className="text-gray-500 text-xs font-mono">Seguro: {new Date(drone.insurance_expiry).toLocaleDateString('pt-BR')}</span>
                {isExpired(drone.insurance_expiry) && <span className="badge-danger">EXPIRADO</span>}
                {isExpiringSoon(drone.insurance_expiry) && !isExpired(drone.insurance_expiry) && <span className="badge-warning">VENCENDO</span>}
              </div>
            )}

            {drone.certifications?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {drone.certifications.map((cert, i) => <span key={i} className="badge-info text-[10px]">{cert}</span>)}
              </div>
            )}

            {/* Items/Equipment list */}
            {drone.items?.length > 0 && (
              <div className="space-y-1.5">
                <span className="text-gray-400 text-[10px] font-mono uppercase flex items-center gap-1"><Package size={10} /> ITENS ({drone.items.length})</span>
                <div className="bg-gray-50 rounded-md p-2 space-y-1">
                  {drone.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 font-medium">{item.name}</span>
                      <div className="flex gap-3 text-gray-400 font-mono">
                        {item.quantity && <span>Qtd: {item.quantity}</span>}
                        {item.serial && <span>S/N: {item.serial}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents list */}
            {drone.documents?.length > 0 && (
              <div className="space-y-1 pt-2 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-mono uppercase">Documentos ({drone.documents.length}):</p>
                {drone.documents.map((doc, i) => (
                  <div key={doc.id || i} className="flex items-center gap-2 group">
                    <a href={`${doc.file_url || doc.url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 text-xs hover:text-gold transition-colors flex-1 min-w-0">
                      <FileText size={12} className="flex-shrink-0" /><span className="truncate">{doc.filename || doc.name}</span>
                    </a>
                    {isSuperAdmin && (
                      <button onClick={async () => {
                        if (!window.confirm('Excluir este documento?')) return;
                        try {
                          await axios.delete(`${API_URL}/drones/${drone.id}/documents/${doc.id}`, { headers });
                          toast.success('Documento excluido!');
                          fetchData();
                        } catch (e) { toast.error('Erro ao excluir'); }
                      }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all" data-testid={`delete-drone-doc-${doc.id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Upload document button */}
            {isSuperAdmin && (
              <div className="pt-2 border-t border-gray-100">
                <input
                  ref={uploadingFor === drone.id ? fileInputRef : null}
                  type="file"
                  className="hidden"
                  id={`doc-upload-${drone.id}`}
                  onChange={(e) => { handleUploadDocument(drone.id, e.target.files[0]); e.target.value = ''; }}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png"
                  data-testid={`upload-drone-doc-${drone.id}`}
                />
                <label
                  htmlFor={`doc-upload-${drone.id}`}
                  className={`flex items-center justify-center gap-2 w-full py-2 px-3 text-xs font-medium rounded-lg cursor-pointer transition-colors border border-dashed ${
                    uploadingFor === drone.id
                      ? 'border-gold/50 bg-gold/5 text-gold'
                      : 'border-gray-300 text-gray-500 hover:border-gold hover:text-gold hover:bg-gold/5'
                  }`}
                >
                  {uploadingFor === drone.id ? (
                    <><div className="w-3 h-3 border-2 border-gold/30 border-t-gold rounded-full animate-spin" /> Enviando...</>
                  ) : (
                    <><Upload size={14} /> ANEXAR DOCUMENTO</>
                  )}
                </label>
              </div>
            )}
          </div>
        ))}
      </div>

      {drones.length === 0 && (
        <div className="card-hud p-8 text-center">
          <Plane size={48} className="text-gray-500 mx-auto mb-4" />
          <p className="text-gray-500 font-mono">Nenhum drone cadastrado</p>
          {isSuperAdmin && (
            <Button className="btn-primary mt-4" onClick={openCreateDialog}><Plus size={18} className="mr-2" />CADASTRAR PRIMEIRO DRONE</Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DronesPage;
